import { ChangeEvent, useCallback, useMemo, useState, FormEvent } from "react";
import { Theme, useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { useAtomicStyles } from "styles";
import { actions, selectors } from "redux-store";
import * as yup from "yup";
import moment, { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { FileManager } from "models";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCurrentMerchantId } from "hooks";
import { useTypedTranslations } from "hooks";

const maxTitleLength = 50;
const maxContentLength = 500;

enum TabsEnum {
  IMAGE,
  TEXTS,
  GENERAL,
}
const titles = ["Immagine", "Descrizione", "Date"];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      backgroundColor: "#eee",
    },
    tabContent: {
      margin: "1em",
    },
    submitEventButton: {
      marginTop: 0,
      marginLeft: "1em",
    },
    "@keyframes shake": {
      "0%": { left: 0 },
      "14%": { left: "-3px" },
      "28%": { left: "5px" },
      "43%": { left: "-8px" },
      "57%": { left: "8px" },
      "71%": { left: "-5px" },
      "86%": { left: "-3px" },
      "100%": { left: 0 },
    },
    submitEventErrorAnimation: {
      animation: "$shake",
      animationDuration: "0.7s",
    },
    tabsIndicator: {
      backgroundColor: theme.palette.primary.main,
    },
    uploaderMobile: {
      maxHeight: 120,
    },
    undoButton: {
      height: "100%",
      width: "100%",
    },
    createButton: {
      margin: 0,
      width: "100%",
      height: "100%",
      position: "relative",
      right: "10px",
    },
    formControlLabel: {
      marginTop: "1em",
    },
  }),
);

const schema = yup.object().shape({
  title: yup
    .string()
    .required("Il titolo è un parametro richiesto")
    .trim()
    .min(3, "Il titolo deve avere almeno 3 caratteri")
    .max(
      maxTitleLength,
      `Il titolo non può superare i ${maxTitleLength} caratteri`,
    ),
  content: yup
    .string()
    .required("Inserisci un contenuto")
    .max(
      maxContentLength,
      `La descrizione non può superare i ${maxContentLength} caratteri`,
    ),
  validFrom: yup.date(),
  expire: yup
    .date()
    .required("Inserisci una scadenza")
    .test({
      name: "min",
      exclusive: false,
      message: "La data di fine non può precedere quella di inizio",
      test: function (expireDate) {
        return moment(expireDate).isAfter(
          moment(this.parent.validFrom ?? moment().subtract(5, "minutes")),
        );
      },
    }),
  imageUri: yup.string().required("Inserisci un'immagine"),
});

type CreateEventFormData = {
  title: string;
  content: string;
  imageUri: string;
  validFrom: Moment;
  expire: Moment;
  isSingleUse: boolean;
};

const useCreateEventForm = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(
    () => ({
      tabs: {
        indicator: styles.tabsIndicator,
      },
    }),
    [styles],
  );
  const atomicStyles = useAtomicStyles();
  const {
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    trigger,
  } = useForm<CreateEventFormData>({
    resolver: yupResolver(schema),
  });

  watch();

  const isLoading = useSelector(
    selectors.getAjaxIsLoadingByApi(
      actions.postMerchantsEventsByMerchantId.api,
    ),
  );
  const [t] = useTypedTranslations();

  const dispatch = useDispatch();
  const merchantId = useCurrentMerchantId();

  const [currentTab, changeCurrentTab] = useState(TabsEnum.IMAGE);
  const [singleUseChecked, setSingleUseChecked] = useState(false);

  const handleSingleUseChange = useCallback(() => {
    setValue("isSingleUse", !singleUseChecked, {
      shouldValidate: false,
      shouldDirty: false,
    });
    setSingleUseChecked(!singleUseChecked);
  }, [setValue, singleUseChecked]);

  const handleTabChange = useCallback(
    (event: ChangeEvent<{}>, newValue: TabsEnum) => {
      changeCurrentTab(newValue);
    },
    [],
  );

  const [imageB64, setImageB64] = useState("");

  const onSubmit = useMemo(
    () =>
      handleSubmit((formData) => {
        const params = {
          merchantId,
          ...formData,
          validFrom:
            formData.validFrom?.toISOString() ?? moment().toISOString(),
          expire: formData.expire.toISOString(),
        };

        dispatch(actions.postMerchantsEventsByMerchantId.request(params));
      }),
    [handleSubmit, merchantId, dispatch],
  );

  const handleDialogClose = useCallback(() => {
    dispatch(actions.setIsCreateEventDialogOpen(false));
  }, [dispatch]);

  const handleChangeImage = useCallback(
    (newB64: string) => {
      setValue("imageUri", newB64, { shouldValidate: true, shouldDirty: true });
      setImageB64(newB64);
    },
    [setValue],
  );

  const uploadLogo = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        setValue("imageUri", "", { shouldValidate: imageB64 !== "" });
        setImageB64("");
        return;
      }

      return FileManager.convertFileToBase64(files[0]).then(
        (pictureBase64: string) => {
          handleChangeImage(pictureBase64);
        },
      );
    },
    [handleChangeImage, imageB64, setValue],
  );

  return {
    styles,
    classes,
    atomicStyles,
    onSubmit,
    register,
    watch,
    control,
    errors,
    TabsEnum,
    currentTab,
    handleTabChange,
    uploadLogo,
    maxTitleLength,
    maxContentLength,
    handleDialogClose,
    imageB64,
    isLoading,
    trigger,
    changeCurrentTab,
    titles,
    t,
    singleUseChecked,
    handleSingleUseChange,
  };
};

export { useCreateEventForm as default };
